import React, { useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Loader2, AlertTriangle, CheckCircle2, Info, ExternalLink,
  Image as ImageIcon, Globe, Sparkles, Twitter, Linkedin, MessageCircle,
} from "lucide-react";

const QUICK_LINKS = [
  { label: "Site default", path: "/" },
  { label: "OG default image", path: "/api/og/default" },
];

export default function SharePreview() {
  const [url, setUrl] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!url.startsWith("http")) {
      toast.error("Enter a full URL (https://…)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/share/preview", { url });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch URL");
    } finally {
      setLoading(false);
    }
  };

  const runQuick = (path) => {
    const base = window.location.origin;
    const final = path.startsWith("/api") ? `${API.replace(/\/api$/, "")}${path}` : `${base}${path}`;
    setUrl(final);
    setTimeout(() => submit(), 0);
  };

  return (
    <div className="space-y-8" data-testid="share-preview-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Marketing tool
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Share preview tester</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Paste any URL — we fetch it the same way Twitter, LinkedIn, WhatsApp and Discord do, parse its
          OG tags, and render side-by-side previews so you A/B-test share titles before campaigns ship.
        </p>
      </header>

      <form onSubmit={submit} className="glass rounded-2xl p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3">
          <Globe className="h-4 w-4 text-white/40" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://makeyourbrand.in/blog/..."
            data-testid="share-preview-url"
            className="flex-1 py-3 text-sm outline-none bg-transparent"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="share-preview-run"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Preview
        </button>
      </form>

      <div className="flex flex-wrap gap-2 text-xs">
        <span className="text-white/40 uppercase tracking-[0.2em] mr-1">Quick test:</span>
        {QUICK_LINKS.map((q) => (
          <button
            key={q.path}
            onClick={() => runQuick(q.path)}
            data-testid={`quick-link-${q.path}`}
            className="glass rounded-lg px-3 py-1.5 hover:bg-white/5"
          >
            {q.label} →
          </button>
        ))}
      </div>

      {data && (
        <>
          {data.issues?.length > 0 && (
            <section className="glass rounded-2xl p-5" data-testid="share-preview-issues">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Health check</div>
              <ul className="space-y-2 text-sm">
                {data.issues.map((iss, i) => {
                  const I = iss.level === "error" ? AlertTriangle : iss.level === "warn" ? Info : CheckCircle2;
                  const color =
                    iss.level === "error" ? "text-rose-300" :
                    iss.level === "warn" ? "text-amber-300" : "text-indigo-300";
                  return (
                    <li key={i} className={`flex items-start gap-2 ${color}`}>
                      <I className="h-4 w-4 mt-0.5 shrink-0" />
                      <span className="text-white/80"><code className="text-xs font-mono opacity-70 mr-2">{iss.key}</code>{iss.msg}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section className="grid lg:grid-cols-2 gap-6" data-testid="share-preview-cards">
            <TwitterPreview data={data} />
            <LinkedInPreview data={data} />
            <WhatsAppPreview data={data} />
            <DiscordPreview data={data} />
          </section>

          <section className="glass rounded-2xl p-5" data-testid="share-preview-meta">
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Parsed metadata</div>
            <div className="grid sm:grid-cols-2 gap-3 text-xs">
              <MetaRow k="URL" v={data.url} link />
              <MetaRow k="Site name" v={data.site_name} />
              <MetaRow k="Title" v={data.title} />
              <MetaRow k="Description" v={data.description} />
              <MetaRow k="Image" v={data.image} link />
              <MetaRow k="Image dimensions" v={data.image_width && data.image_height ? `${data.image_width} × ${data.image_height}` : "—"} />
              <MetaRow k="Twitter card" v={data.twitter_card} />
              <MetaRow k="OG type" v={data.og_type} />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function MetaRow({ k, v, link }) {
  if (!v) return (
    <div className="glass rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{k}</div>
      <div className="text-white/30 mt-1">—</div>
    </div>
  );
  return (
    <div className="glass rounded-lg p-3" data-testid={`meta-${k.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{k}</div>
      {link ? (
        <a href={v} target="_blank" rel="noreferrer" className="text-indigo-300 break-all hover:underline inline-flex items-center gap-1 mt-1">
          {v} <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
        </a>
      ) : (
        <div className="text-white/80 break-words mt-1">{v}</div>
      )}
    </div>
  );
}

/* ------------- Platform preview components ------------- */
function PreviewShell({ icon: Icon, label, accent, children }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={`preview-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${accent}`} />
        <div className="text-xs uppercase tracking-[0.2em] text-white/40">{label} preview</div>
      </div>
      {children}
    </div>
  );
}

function ImageBlock({ src, ratio = "aspect-[1200/630]", fallbackText }) {
  if (!src) {
    return (
      <div className={`${ratio} bg-white/5 grid place-items-center text-white/30 text-sm`}>
        <div className="flex flex-col items-center gap-2"><ImageIcon className="h-6 w-6" />{fallbackText || "No image"}</div>
      </div>
    );
  }
  return (
    <div className={`${ratio} bg-white/5 overflow-hidden`}>
      <img src={src} alt="" aria-label="share preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
    </div>
  );
}

function TwitterPreview({ data }) {
  const title = (data.title || "").slice(0, 70);
  return (
    <PreviewShell icon={Twitter} label="Twitter" accent="text-sky-400">
      <div className="rounded-2xl overflow-hidden bg-black border border-white/10">
        <ImageBlock src={data.image} />
        <div className="p-3 border-t border-white/10 bg-black">
          <div className="text-xs text-white/40 lowercase">{data.host}</div>
          <div className="text-white text-sm font-semibold mt-0.5 leading-tight">{title}</div>
        </div>
      </div>
    </PreviewShell>
  );
}

function LinkedInPreview({ data }) {
  return (
    <PreviewShell icon={Linkedin} label="LinkedIn" accent="text-blue-500">
      <div className="rounded-lg overflow-hidden bg-white border border-white/10 text-black">
        <ImageBlock src={data.image} />
        <div className="p-3 bg-[#F3F2EF] text-black">
          <div className="font-semibold text-[15px] leading-snug">{data.title || "(no title)"}</div>
          <div className="text-xs text-[#666] mt-1 uppercase">{data.host}</div>
        </div>
      </div>
    </PreviewShell>
  );
}

function WhatsAppPreview({ data }) {
  // WhatsApp uses a short tiny preview that's title + 1 line of description + small image (left)
  const desc = (data.description || "").slice(0, 65) + ((data.description || "").length > 65 ? "…" : "");
  const title = (data.title || "").slice(0, 60);
  return (
    <PreviewShell icon={MessageCircle} label="WhatsApp" accent="text-emerald-400">
      <div className="rounded-xl overflow-hidden bg-[#005C4B] border border-white/10 max-w-sm">
        {/* WA shows a large image on top when og:image is present */}
        <ImageBlock src={data.image} ratio="aspect-[1200/630]" />
        <div className="p-3 text-white">
          <div className="font-semibold text-sm leading-tight">{title || "(no title)"}</div>
          {desc && <div className="text-xs text-white/80 mt-1">{desc}</div>}
          <div className="text-[10px] text-white/50 mt-1.5">{data.host}</div>
        </div>
      </div>
    </PreviewShell>
  );
}

function DiscordPreview({ data }) {
  return (
    <PreviewShell icon={MessageCircle} label="Discord" accent="text-violet-400">
      <div className="rounded-md overflow-hidden bg-[#2B2D31] border-l-4 border-indigo-500 p-3 max-w-md">
        <div className="text-xs text-white/60">{data.site_name || data.host}</div>
        <a href={data.url} target="_blank" rel="noreferrer" className="block text-indigo-300 font-semibold text-sm mt-1 hover:underline">{data.title || "(no title)"}</a>
        {data.description && <div className="text-xs text-white/75 mt-1.5 leading-relaxed">{data.description}</div>}
        {data.image && (
          <div className="mt-2 rounded overflow-hidden">
            <ImageBlock src={data.image} ratio="aspect-[1200/630]" />
          </div>
        )}
      </div>
    </PreviewShell>
  );
}
